/**
 * Deterministic, local, culinary-process-aware structuring for
 * PROCESS_STORY's source-first authoring flow. No provider call, no
 * network — pure text processing. Provider calls = 0, AI cost = $0.
 *
 * ── ALGORITHM ─────────────────────────────────────────────────────────
 * The source is split into paragraphs on blank-line boundaries (same
 * mechanism as editorialSimplePilot.mjs's chunking). A paragraph is
 * treated as the START of a new phase when its FIRST line matches a
 * short "Heading: rest of first sentence" pattern — short (<=~38
 * chars before the colon), starts with a capital letter, and contains
 * no sentence-ending punctuation in the heading portion itself (so an
 * ordinary sentence that happens to contain a colon mid-way is never
 * mistaken for a section heading). This deliberately does NOT hardcode
 * a fixed vocabulary (Опара/Тесто/Формовка/…) — ANY paragraph whose
 * first line fits that shape becomes a phase, matching the confirmed
 * requirement that the recognizer generalize beyond a fixed list.
 *
 * Paragraphs before the first detected heading become `introduction`
 * — but only if they actually read like prose (contain at least one
 * of . ! ? …). A pre-heading chunk that doesn't look like prose (e.g.
 * a bare ingredient list with no sentence) is never discarded and
 * never mislabeled as introduction: it is folded into the first
 * phase's text instead.
 *
 * A paragraph that does NOT match the heading pattern but appears
 * AFTER at least one phase has already started is treated as a
 * continuation of the CURRENT (most recent) phase — appended to its
 * text — rather than silently dropped, and rather than being
 * (mis)classified as a trailing "conclusion". This is a deliberately
 * conservative choice: distinguishing a genuine standalone closing
 * summary from "just more of the last phase" is ambiguous without
 * stronger signals, and losing source text is worse than a slightly
 * longer final phase. `conclusion` is therefore always returned as
 * "" by this function — the caller (EditorialProcessStoryV2.jsx)
 * carries forward whatever the author has already typed there across
 * re-recognition, the same way HERO/EDITORIAL_STORY already carry
 * forward their own parser-blind manual fields (eyebrow).
 *
 * Nothing here EVER breaks a sentence into its own phase, and nothing
 * here turns an ingredient quantity into a separate phase — only a
 * genuine heading-shaped first line starts a new phase.
 *
 * ── PARAMETER EXTRACTION ─────────────────────────────────────────────
 * A concise operational parameter is extracted from each phase's text
 * when explicitly present — temperature+time combined ("200°C · 12
 * минут"), a time range ("10–12 минут"), a single time value ("2
 * часа"), or temperature alone — in that priority order (most
 * operationally useful first). Nothing is invented: if none of these
 * patterns are found, parameter is "".
 *
 * `label` is always "" — recognition never invents editorial labels
 * like "Фаза 1"/"Этап 2"; that field is manual-only, same treatment
 * as HERO/EDITORIAL_STORY's eyebrow.
 */

function compact(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function looksLikeProse(text) {
  return /[.!?…]/.test(text);
}

function splitParagraphs(text) {
  return String(text || "")
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

const HEADING_LINE_RE = /^([\p{Lu}][^\n:.!?…]{0,38}):\s*(.*)$/u;

function matchHeading(paragraph) {
  const firstNewline = paragraph.indexOf("\n");
  const firstLine = firstNewline === -1 ? paragraph : paragraph.slice(0, firstNewline);
  const rest = firstNewline === -1 ? "" : paragraph.slice(firstNewline + 1);
  const m = HEADING_LINE_RE.exec(firstLine.trim());
  if (!m) return null;
  const heading = compact(m[1]);
  if (!heading) return null;
  return { heading, afterColon: compact(m[2]), restLines: rest };
}

function buildPhaseText(afterColon, restLines) {
  const lines = restLines ? restLines.split("\n") : [];
  const parts = [afterColon, ...lines].map(compact).filter(Boolean);
  return parts.join(" ");
}

const TEMP_TIME_RE = /(\d{2,3})\s*°\s*C[^.!?\n]{0,40}?(\d{1,3}(?:[–-]\d{1,3})?)\s*(минут|мин\.?|часа?|часов|ч\.)/iu;
const RANGE_TIME_RE = /(\d{1,3}[–-]\d{1,3})\s*(минут|мин\.?|часа?|часов|ч\.)/iu;
const SINGLE_TIME_RE = /(\d{1,3})\s*(минут|мин\.?|часа?|часов|ч\.)/iu;
const TEMP_ONLY_RE = /(\d{2,3})\s*°\s*C/iu;

function normalizeUnit(raw) {
  const u = String(raw || "").toLowerCase();
  if (u.startsWith("мин")) return "минут";
  if (u.startsWith("час") || u === "ч." || u === "ч") return "часа";
  return raw;
}

function extractParameter(text) {
  const tempTime = TEMP_TIME_RE.exec(text);
  if (tempTime) return `${tempTime[1]}°C · ${tempTime[2]} ${normalizeUnit(tempTime[3])}`;
  const range = RANGE_TIME_RE.exec(text);
  if (range) return `${range[1]} ${normalizeUnit(range[2])}`;
  const single = SINGLE_TIME_RE.exec(text);
  if (single) return `${single[1]} ${normalizeUnit(single[2])}`;
  const tempOnly = TEMP_ONLY_RE.exec(text);
  if (tempOnly) return `${tempOnly[1]}°C`;
  return "";
}

let idSeq = 0;
function newPhaseId() {
  idSeq += 1;
  return `phase-auto-${Date.now().toString(36)}-${idSeq}`;
}

export function structureProcessStorySource(rawText) {
  const paragraphs = splitParagraphs(rawText);
  if (!paragraphs.length) return { introduction: "", phases: [], conclusion: "" };

  const introChunks = [];
  const phases = [];
  let sawHeading = false;

  for (const paragraph of paragraphs) {
    const match = matchHeading(paragraph);
    if (match) {
      sawHeading = true;
      const text = buildPhaseText(match.afterColon, match.restLines);
      phases.push({
        id: newPhaseId(),
        label: "",
        title: match.heading,
        text,
        parameter: extractParameter(text),
      });
      continue;
    }

    if (sawHeading && phases.length) {
      const last = phases[phases.length - 1];
      last.text = compact(`${last.text} ${compact(paragraph)}`);
      if (!last.parameter) last.parameter = extractParameter(last.text);
    } else {
      introChunks.push(paragraph);
    }
  }

  const introCandidate = compact(introChunks.join(" "));
  let introduction = "";
  if (introCandidate) {
    if (looksLikeProse(introCandidate) || !phases.length) {
      introduction = introCandidate;
    } else {
      phases[0].text = compact(`${introCandidate} ${phases[0].text}`);
    }
  }

  return { introduction, phases, conclusion: "" };
}
